/**
 * Statussen en tijdlijn van een softwaremelding (besluit Tom 01-10).
 *
 * 'onvolledig' = de melding wacht op de melder: de AI vond hem niet
 * naspeelbaar en de melder sloeg de doorvraag over, óf Tom stelde een vraag
 * terug. De tijdlijn van vragen en antwoorden staat in
 * `doorvraag.vragen_terug`. Beantwoordt de melder, dan wacht de melding op
 * Tom; de database zet hem dan (via de cron) terug op 'nieuw'. Tot die tijd
 * tonen de schermen de afgeleide stand 'beantwoord'.
 *
 * De regel "laatste item is een antwoord van de melder" staat ook in SQL
 * (melding_is_beantwoord) — houd beide gelijk.
 */
export type MeldingStatus = 'nieuw' | 'gepland' | 'afgewezen' | 'opgelost' | 'onvolledig';

export type TijdlijnSoort = 'ai' | 'tom' | 'melder' | 'herinnering';

export interface TijdlijnItem {
  soort: TijdlijnSoort;
  tekst: string;
  op: string;
  door?: string | null;
}

export interface DoorvraagVraag {
  vraag: string;
  opties?: string[];
  antwoord?: string;
}

export interface MeldingDoorvraag {
  sessie_id?: string;
  vragen?: DoorvraagVraag[];
  beoordeling?: { score: number | null; waarom: string | null; rondes: number };
  vragen_terug?: TijdlijnItem[];
}

/** Uitkomst van de AI-duidelijkheidscheck (edge function melding-duidelijkheid). */
export interface Beoordeling {
  duidelijk: boolean;
  score: number | null;
  waarom: string | null;
  vragen: DoorvraagVraag[];
  /** Gezet als er geen echte beoordeling was (limiet, sleutel, AI-fout). */
  fout?: string;
  sessieId?: string;
}

/** Na de eerste check mag de AI nog twee keer herbeoordelen. */
export const MAX_HERBEOORDELINGEN = 2;

export const STATUS_LABEL_MELDER: Record<
  MeldingStatus | 'beantwoord',
  { label: string; klasse: string }
> = {
  nieuw: { label: 'Ingestuurd', klasse: 'bg-info/10 text-info' },
  gepland: { label: 'Wordt opgepakt', klasse: 'bg-success/10 text-success' },
  afgewezen: { label: 'Niet opgepakt', klasse: 'bg-surface-secondary text-foreground-tertiary' },
  opgelost: { label: 'Opgelost', klasse: 'bg-success text-white' },
  onvolledig: { label: 'Aanvulling gevraagd', klasse: 'bg-warning/15 text-warning' },
  beantwoord: { label: 'Aangevuld — bij Tom', klasse: 'bg-info/10 text-info' },
};

export const STATUS_LABEL_BEHEERDER: Record<
  MeldingStatus | 'beantwoord',
  { label: string; klasse: string }
> = {
  nieuw: { label: 'Ingestuurd', klasse: 'bg-info/10 text-info' },
  gepland: { label: 'Wordt opgepakt', klasse: 'bg-success/10 text-success' },
  opgelost: { label: 'Opgelost', klasse: 'bg-success text-white' },
  afgewezen: { label: 'Niet opgepakt', klasse: 'bg-surface-secondary text-foreground-tertiary' },
  onvolledig: { label: 'Onvolledig — wacht op melder', klasse: 'bg-warning/15 text-warning' },
  beantwoord: { label: 'Aangevuld door melder', klasse: 'bg-brand/10 text-brand' },
};

export function leesDoorvraag(doorvraag: unknown): MeldingDoorvraag {
  return doorvraag && typeof doorvraag === 'object' ? (doorvraag as MeldingDoorvraag) : {};
}

export function tijdlijn(doorvraag: unknown): TijdlijnItem[] {
  const items = leesDoorvraag(doorvraag).vragen_terug;
  return Array.isArray(items) ? items.filter((i) => i && typeof i.tekst === 'string') : [];
}

/** Laatste item (herinneringen niet meegerekend) is een antwoord van de melder. */
export function isBeantwoord(doorvraag: unknown): boolean {
  const relevant = tijdlijn(doorvraag).filter((i) => i.soort !== 'herinnering');
  return relevant.length > 0 && relevant[relevant.length - 1].soort === 'melder';
}

/** De vraag of reden waar de melder nu op moet reageren (laatste van ai/tom). */
export function openVraag(doorvraag: unknown): TijdlijnItem | null {
  const vragen = tijdlijn(doorvraag).filter((i) => i.soort === 'ai' || i.soort === 'tom');
  return vragen.length ? vragen[vragen.length - 1] : null;
}

/**
 * Wat een scherm toont: een onvolledige melding waarop de melder al
 * geantwoord heeft, is 'beantwoord' tot de database hem op 'nieuw' zet.
 */
export function effectieveStatus(m: {
  status: string;
  doorvraag: unknown;
}): MeldingStatus | 'beantwoord' {
  if (m.status === 'onvolledig' && isBeantwoord(m.doorvraag)) return 'beantwoord';
  return m.status as MeldingStatus;
}

/** Telt als open werk voor Tom (nieuw, of aangevuld door de melder). */
export function isOpenVoorBeheerder(m: { status: string; doorvraag: unknown }): boolean {
  const s = effectieveStatus(m);
  return s === 'nieuw' || s === 'beantwoord';
}

const NIETSZEGGEND = new Set([
  '',
  '-',
  '?',
  'nvt',
  'n.v.t.',
  'geen idee',
  'weet ik niet',
  'weet niet',
  'weet ik niet meer',
  'onbekend',
  'geen',
]);

/** "Weet ik niet", leeg of een streepje: geen informatie. */
export function isAntwoordNietszeggend(antwoord: string | undefined | null): boolean {
  const schoon = (antwoord ?? '')
    .trim()
    .toLowerCase()
    .replace(/[.!…?]+$/g, '')
    .replace(/\./g, '')
    .trim();
  return NIETSZEGGEND.has(schoon);
}

export function heeftBruikbaarAntwoord(vragen: DoorvraagVraag[]): boolean {
  return vragen.some((v) => !isAntwoordNietszeggend(v.antwoord));
}

export type Vervolg =
  | { actie: 'insturen'; status: 'nieuw' }
  | { actie: 'insturen'; status: 'onvolledig' }
  | { actie: 'doorvragen' };

/**
 * Statusovergang na een beoordeling. `ronde` = hoeveel herbeoordelingen er
 * al geweest zijn (0 = eerste check).
 *  - duidelijk, of geen echte beoordeling (fout/limiet): direct insturen als 'nieuw'
 *  - onduidelijk met vragen en nog rondes over: doorvragen
 *  - anders (geen vragen, of rondes op): insturen als 'onvolledig'
 */
export function beslisVervolg(beoordeling: Beoordeling, ronde: number): Vervolg {
  if (beoordeling.duidelijk || beoordeling.fout) return { actie: 'insturen', status: 'nieuw' };
  if (ronde < MAX_HERBEOORDELINGEN && beoordeling.vragen.length > 0) return { actie: 'doorvragen' };
  return { actie: 'insturen', status: 'onvolledig' };
}

/**
 * De doorvraag-kolom bij het insturen: alle gestelde vragen met antwoorden,
 * de laatste beoordeling, en bij 'onvolledig' de reden als eerste
 * tijdlijn-item (daar reageert de melder later op).
 */
export function bouwDoorvraagVoorInsturen(invoer: {
  sessieId: string | null;
  vragen: DoorvraagVraag[];
  beoordeling: Beoordeling | null;
  rondes: number;
  status: 'nieuw' | 'onvolledig';
  nu?: Date;
}): MeldingDoorvraag | null {
  const { sessieId, vragen, beoordeling, rondes, status } = invoer;
  if (!beoordeling && vragen.length === 0) return null;
  const doorvraag: MeldingDoorvraag = {};
  if (sessieId) doorvraag.sessie_id = sessieId;
  if (vragen.length) doorvraag.vragen = vragen;
  if (beoordeling && !beoordeling.fout) {
    doorvraag.beoordeling = { score: beoordeling.score, waarom: beoordeling.waarom, rondes };
  }
  if (status === 'onvolledig') {
    doorvraag.vragen_terug = [
      {
        soort: 'ai',
        tekst:
          beoordeling?.waarom?.trim() ||
          'Er is meer informatie nodig om deze melding na te spelen.',
        op: (invoer.nu ?? new Date()).toISOString(),
      },
    ];
  }
  return doorvraag;
}
